import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Download, Loader2, Hash, Building2, MapPin, User,
  CreditCard, FileText, Fingerprint, Calendar, Clock, ChevronLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAgreementStore } from '@/modules/agreements/store/agreementStore';
import { useCollectDetail } from '../hooks/useCollectDetail';
import { maskCPF, formatCPF } from '../utils/formatters';

const INPI_NUMBER = '5120180003362';

interface DetailRowProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

function DetailRow({ icon: Icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 flex-none text-slate-400" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
        <p className="break-words text-sm font-semibold text-slate-900 dark:text-slate-100">{value}</p>
      </div>
    </div>
  );
}

const CollectDetailPage: React.FC = () => {
  const { nsr } = useParams<{ nsr: string }>();
  const navigate = useNavigate();
  const { activeAgreement } = useAgreementStore();
  const { collect, employer, employee, loading, error, pdfLoading, pdfError, handleDownloadPDF } =
    useCollectDetail(nsr, activeAgreement?.uuid);

  const employerName = employer?.name || employer?.contractor?.name || employer?.contractor || '';
  const employerDoc = employer?.docValue || employer?.contractor?.docValue || '';
  const employerAddress =
    employer?.mainAddress?.label || employer?.contractor?.mainAddress?.label || employer?.address || '';
  const employeeName = employee?.name || '';
  const employeeCPF = formatCPF(employee?.cpf || '');
  const hash = collect?.hash || '';

  return (
    <main
      className="mx-auto max-w-md px-4 py-6"
      id="main-content"
      role="main"
      aria-label="Detalhe do registro de ponto"
    >
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="-ml-2 mb-4 gap-1 text-slate-600"
        aria-label="Voltar"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        Voltar
      </Button>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <h1 className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-100">
          Comprovante
        </h1>

        {loading && (
          <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" aria-label="Carregando" />
          </div>
        )}

        {error && !loading && (
          <div
            role="alert"
            aria-live="assertive"
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-4">
            <div className="space-y-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800">
              <DetailRow icon={Hash} label="Sequencial" value={String(collect?.nsr ?? nsr ?? '-')} />
              {collect?.date && (
                <DetailRow
                  icon={Calendar}
                  label="Data"
                  value={new Date(collect.date).toLocaleDateString('pt-BR')}
                />
              )}
              {collect?.time && (
                <DetailRow icon={Clock} label="Hora" value={collect.time.slice(0, 5)} />
              )}
            </div>

            {(employerName || employerDoc || employerAddress) && (
              <>
                <Separator />
                <div className="space-y-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800">
                  {employerName && <DetailRow icon={Building2} label="Empregador" value={employerName} />}
                  {employerDoc && <DetailRow icon={Hash} label="CNPJ" value={employerDoc} />}
                  {employerAddress && <DetailRow icon={MapPin} label="Endereço" value={employerAddress} />}
                </div>
              </>
            )}

            {(employeeName || employeeCPF) && (
              <>
                <Separator />
                <div className="space-y-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800">
                  {employeeName && <DetailRow icon={User} label="Empregado" value={employeeName} />}
                  {employeeCPF && <DetailRow icon={CreditCard} label="CPF" value={maskCPF(employeeCPF)} />}
                </div>
              </>
            )}

            <Separator />
            <div className="space-y-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800">
              <DetailRow icon={FileText} label="INPI" value={INPI_NUMBER} />
              {hash && <DetailRow icon={Fingerprint} label="Hash" value={hash} />}
            </div>

            <Button
              className="mt-2 w-full"
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
              aria-label="Baixar comprovante em PDF"
            >
              {pdfLoading
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                : <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              }
              {pdfLoading ? 'Gerando PDF...' : 'Baixar comprovante (PDF)'}
            </Button>

            {pdfError && (
              <div
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {pdfError}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default CollectDetailPage;
